import IMultiSelectable from '@/types/Atomic/IMultiSelectable';

/**
 * A CSS font-family value split into the primary family and its fallbacks,
 * e.g. `'Open Sans', Arial, serif` → `{ family: 'Open Sans', fallbacks: ['Arial', 'serif'] }`.
 */
export interface ParsedFontStyle {
    family: string;
    fallbacks: string[];
}

const unquote = (name: string): string => name.replace(/^(['"])(.*)\1$/, '$2');

const quote = (name: string): string =>
    name.includes(' ') ? `'${name}'` : name;

export const parseFontStyle = (style: string): ParsedFontStyle => {
    const names = style
        .split(',')
        .map((name) => unquote(name.trim()).trim())
        .filter((name) => name !== '');

    return {
        family: names[0] ?? '',
        fallbacks: names.slice(1),
    };
};

export const buildFontStyle = (family: string, fallbacks: string[]): string =>
    [...new Set([family, ...fallbacks])].map(quote).join(', ');

/**
 * Picks the option objects matching the given fallback names, so components
 * comparing options by reference show them as selected. Matching ignores case,
 * as CSS font family names do, and duplicates are selected once. Names without
 * a matching option are appended as new options instead of being dropped.
 */
export const selectFallbackOptions = (
    options: IMultiSelectable[],
    fallbacks: string[]
): { options: IMultiSelectable[]; selected: IMultiSelectable[] } => {
    const merged = [...options];
    const selected: IMultiSelectable[] = [];

    fallbacks.forEach((title) => {
        let option = merged.find(
            (candidate) =>
                candidate.title.toLowerCase() === title.toLowerCase()
        );

        if (!option) {
            option = { title };
            merged.push(option);
        }

        if (!selected.includes(option)) {
            selected.push(option);
        }
    });

    return { options: merged, selected };
};
